import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Avatar,
  Divider,
} from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import RefreshIcon from "@material-ui/icons/Refresh";
import StarIcon from "@material-ui/icons/Star";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  scoreCard: {
    textAlign: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    minHeight: 180,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  scoreNumber: {
    fontSize: "4rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  scoreLabel: {
    fontSize: "1.2rem",
    opacity: 0.9,
  },
  componentCard: {
    height: "100%",
  },
  componentName: {
    fontSize: "0.9rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  componentScore: {
    fontSize: "2rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  componentProgress: {
    height: 8,
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
  componentDetails: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    maxHeight: 500,
  },
  productRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  productImage: {
    width: 40,
    height: 40,
    marginRight: theme.spacing(1),
  },
  productInfo: {
    display: "flex",
    alignItems: "center",
  },
  scoreChip: {
    fontWeight: "bold",
  },
  calculateButton: {
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  errorText: {
    color: theme.palette.error.main,
    textAlign: "center",
    padding: theme.spacing(3),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
});

const hebrewLabels = {
  title: "ניקוד ביצועים",
  overallScore: "ניקוד כולל",
  components: "רכיבי הניקוד",
  topProducts: "מוצרים מובילים",
  calculateAll: "חשב מחדש ניקוד לכל המוצרים",
  calculating: "מפעיל חישוב...",
  calculateDescription: "חישוב הניקוד לכל המוצרים במלאי ברקע (עלול לקחת מספר דקות)",
  componentNames: {
    salesVolume: "נפח מכירות (30 יום)",
    conversionRate: "שיעור המרה",
    revenueImpact: "השפעת הכנסות",
    stockHealth: "בריאות מלאי",
    profitMargin: "רווחיות",
    engagementGrowth: "צמיחה",
  },
  tableHeaders: {
    rank: "דירוג",
    product: "מוצר",
    score: "ניקוד",
    sales: "מכירות (30 יום)",
    stock: "מלאי",
    price: "מחיר",
  },
  loading: "טוען נתונים...",
  error: "שגיאה בטעינת הנתונים",
  noData: "אין נתוני ניקוד זמינים",
  calculationStarted: "החישוב התחיל ברקע. זה עשוי לקחת מספר דקות.",
  outOf: "מתוך 100",
};

class ProductScoringPanel extends React.Component {
  _isMounted = false;

  state = {
    loading: true,
    error: null,
    scoreData: null,
    topProducts: [],
    calculating: false,
  };

  componentDidMount() {
    this._isMounted = true;
    this.loadScoringData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.product?.ean !== this.props.product?.ean) {
      this.loadScoringData();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  loadScoringData = async () => {
    const { product } = this.props;
    if (!product?.ean) return;

    this.setState({ loading: true, error: null });

    try {
      // Fetch product score and top products in parallel
      const adminHeaders = utils.getAdminAuthHeaders();
      const [scoreRes, topRes] = await Promise.all([
        fetch(`${basePath}/api/v1/admin/product-analytics/${product.ean}/score`, {
          headers: adminHeaders,
        }),
        fetch(`${basePath}/api/v1/admin/product-analytics/top-products?limit=10`, {
          headers: adminHeaders,
        }),
      ]);

      if (!scoreRes.ok || !topRes.ok) {
        throw new Error("Failed to fetch scoring data");
      }

      const [scoreData, topData] = await Promise.all([scoreRes.json(), topRes.json()]);

      if (this._isMounted) {
        this.setState({
          loading: false,
          scoreData,
          topProducts: topData.products || [],
        });
      }
    } catch (error) {
      console.error("Error loading scoring data:", error);
      if (this._isMounted) {
        this.setState({
          loading: false,
          error: error.message,
        });
      }
    }
  };

  handleCalculateAll = async () => {
    // Confirm before starting expensive operation
    if (!window.confirm(
      "חישוב הניקוד לכל המוצרים עלול לקחת מספר דקות.\n" +
      "הפעולה תרוץ ברקע ותוכל להמשיך לעבוד.\n" +
      "האם להמשיך?"
    )) {
      return;
    }

    this.setState({ calculating: true });

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/product-analytics/calculate-all-scores`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...utils.getAdminAuthHeaders(),
          },
          body: JSON.stringify({ batchSize: 50 }), // Smaller batches for better progress
        }
      );

      if (!response.ok) {
        throw new Error("Failed to start calculation");
      }

      const data = await response.json();

      alert(
        `✅ החישוב התחיל בהצלחה!\n\n` +
        `מספר מוצרים: ${data.totalProducts}\n` +
        `זמן משוער: ${data.estimatedTimeMinutes} דקות\n` +
        `מזהה ריצה: ${data.runId}\n\n` +
        `הניקוד יתעדכן אוטומטית. תוכל להמשיך לעבוד בינתיים.`
      );

      if (this._isMounted) {
        this.setState({ calculating: false });
      }

      // Reload data after estimated time (with buffer)
      const reloadDelay = Math.max(10000, data.estimatedTimeMinutes * 60 * 1000);
      setTimeout(() => {
        if (this._isMounted) {
          this.loadScoringData();
        }
      }, reloadDelay);
    } catch (error) {
      console.error("Error calculating scores:", error);
      alert(`❌ שגיאה: ${error.message}`);
      if (this._isMounted) {
        this.setState({ calculating: false });
      }
    }
  };

  getScoreColor = (score) => {
    if (score >= 80) return "#4caf50";
    if (score >= 60) return "#ff9800";
    if (score >= 40) return "#ff5722";
    return "#f44336";
  };

  getScoreRating = (score) => {
    if (score >= 80) return { label: "מצוין", color: "success" };
    if (score >= 60) return { label: "טוב", color: "primary" };
    if (score >= 40) return { label: "ממוצע", color: "warning" };
    return { label: "נמוך", color: "error" };
  };

  formatNumber = (num) => {
    return num?.toLocaleString() || "0";
  };

  formatCurrency = (num) => {
    return `₪${num?.toLocaleString() || 0}`;
  };

  getProductImageUrl = (product) => {
    if (product.cdn?.mainImage) {
      return product.cdn.mainImage.small || product.cdn.mainImage.medium;
    }
    return null;
  };

  renderScoreBreakdown() {
    const { classes } = this.props;
    const { scoreData } = this.state;

    if (!scoreData || !scoreData.components) {
      return null;
    }

    const components = [
      {
        key: "salesVolume",
        data: scoreData.components.salesVolume,
        color: "#1976d2",
        description: "יחידות שנמכרו ב-30 ימים אחרונים",
      },
      {
        key: "conversionRate",
        data: scoreData.components.conversionRate,
        color: "#2e7d32",
        description: "יחס הזמנות לצפיות",
      },
      {
        key: "revenueImpact",
        data: scoreData.components.revenueImpact,
        color: "#ed6c02",
        description: "סה״כ הכנסות ב-30 ימים",
      },
      {
        key: "stockHealth",
        data: scoreData.components.stockHealth,
        color: "#9c27b0",
        description: "איזון מלאי ותחלופה",
      },
      {
        key: "profitMargin",
        data: scoreData.components.profitMargin,
        color: "#d32f2f",
        description: "אחוז רווח נוכחי",
      },
      {
        key: "engagementGrowth",
        data: scoreData.components.engagementGrowth,
        color: "#0288d1",
        description: "מגמת צמיחה (30 ימים)",
      },
    ];

    return (
      <Grid container spacing={3}>
        {components.map((component) => (
          <Grid item xs={12} sm={6} md={4} key={component.key}>
            <Card className={classes.componentCard}>
              <CardContent>
                <Typography className={classes.componentName}>
                  {hebrewLabels.componentNames[component.key]}
                </Typography>
                <Typography
                  className={classes.componentScore}
                  style={{ color: component.color }}
                >
                  {typeof component.data?.score === 'number' 
                    ? component.data.score.toFixed(0) 
                    : '0'}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={component.data?.score || 0}
                  className={classes.componentProgress}
                  style={{
                    backgroundColor: `${component.color}20`,
                  }}
                  classes={{
                    bar: classes.progressBar,
                  }}
                  sx={{
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: component.color,
                    },
                  }}
                />
                <Typography className={classes.componentDetails}>
                  {component.description}
                </Typography>
                {component.data?.value !== undefined && (
                  <Typography className={classes.componentDetails} style={{ marginTop: 4 }}>
                    ערך: {this.formatNumber(component.data.value)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  renderTopProductsTable() {
    const { classes } = this.props;
    const { topProducts } = this.state;

    if (!topProducts || topProducts.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          {hebrewLabels.noData}
        </Typography>
      );
    }

    return (
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{hebrewLabels.tableHeaders.rank}</TableCell>
              <TableCell>{hebrewLabels.tableHeaders.product}</TableCell>
              <TableCell align="center">{hebrewLabels.tableHeaders.score}</TableCell>
              <TableCell align="right">{hebrewLabels.tableHeaders.sales}</TableCell>
              <TableCell align="right">{hebrewLabels.tableHeaders.stock}</TableCell>
              <TableCell align="right">{hebrewLabels.tableHeaders.price}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topProducts.map((product, index) => {
              const imageUrl = this.getProductImageUrl(product);
              const scoreRating = this.getScoreRating(product.score);

              return (
                <TableRow key={product.ean} className={classes.productRow}>
                  <TableCell>
                    <Chip
                      icon={index < 3 ? <StarIcon /> : null}
                      label={index + 1}
                      size="small"
                      color={index < 3 ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <div className={classes.productInfo}>
                      {imageUrl ? (
                        <Avatar
                          src={imageUrl}
                          variant="rounded"
                          className={classes.productImage}
                        />
                      ) : (
                        <Avatar variant="rounded" className={classes.productImage}>
                          {product.brand?.charAt(0) || "?"}
                        </Avatar>
                      )}
                      <div>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {product.displayName || product.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {product.brand} • {product.displaySize}
                        </Typography>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={typeof product.score === 'number' 
                        ? product.score.toFixed(0) 
                        : '0'}
                      size="small"
                      className={classes.scoreChip}
                      style={{
                        backgroundColor: this.getScoreColor(product.score || 0),
                        color: "#fff",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {this.formatNumber(product.unitsSold30d || 0)}
                  </TableCell>
                  <TableCell align="right">
                    {this.formatNumber(product.stock || 0)}
                  </TableCell>
                  <TableCell align="right">
                    {this.formatCurrency(product.price)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  render() {
    const { classes, product } = this.props;
    const { loading, error, scoreData, calculating } = this.state;

    if (!product) return null;

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return (
        <Typography className={classes.errorText}>
          {hebrewLabels.error}: {error}
        </Typography>
      );
    }

    const score = typeof scoreData?.score === 'number' ? scoreData.score : 0;
    const scoreRating = this.getScoreRating(score);

    return (
      <div className={classes.root}>
        {/* Overall Score */}
        <Paper className={classes.paper}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card className={classes.scoreCard}>
                <CardContent>
                  <Typography className={classes.scoreNumber}>
                    {Number(score).toFixed(0)}
                  </Typography>
                  <Typography className={classes.scoreLabel}>
                    {hebrewLabels.overallScore}
                  </Typography>
                  <Chip
                    label={scoreRating.label}
                    color={scoreRating.color}
                    style={{ marginTop: 16 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box p={2}>
                <Typography variant="h6" gutterBottom>
                  {hebrewLabels.components}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  הניקוד מחושב על בסיס 6 מדדים מותאמים לעסק: נפח מכירות (35%), המרה (20%), הכנסות (15%), מלאי (15%), רווחיות (10%), צמיחה (5%)
                </Typography>
                <Box mt={2} p={2} bgcolor="rgba(25, 118, 210, 0.08)" borderRadius={1}>
                  <Typography variant="body2" color="textSecondary" paragraph style={{ margin: 0, marginBottom: 8 }}>
                    {hebrewLabels.calculateDescription}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={calculating ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                    onClick={this.handleCalculateAll}
                    disabled={calculating}
                  >
                    {calculating
                      ? hebrewLabels.calculating
                      : hebrewLabels.calculateAll}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Score Breakdown */}
        <Paper className={classes.paper}>
          <Box className={classes.sectionHeader}>
            <div className={classes.sectionTitle}>
              <TrendingUpIcon color="primary" />
              <Typography variant="h6">{hebrewLabels.components}</Typography>
            </div>
          </Box>
          <Divider />
          <Box mt={3}>{this.renderScoreBreakdown()}</Box>
        </Paper>

        {/* Top Products Table */}
        <Paper className={classes.paper}>
          <Box className={classes.sectionHeader}>
            <div className={classes.sectionTitle}>
              <StarIcon color="primary" />
              <Typography variant="h6">{hebrewLabels.topProducts}</Typography>
            </div>
          </Box>
          <Divider />
          <Box mt={2}>{this.renderTopProductsTable()}</Box>
        </Paper>
      </div>
    );
  }
}

ProductScoringPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  product: PropTypes.object,
};

export default withStyles(styles)(ProductScoringPanel);
